<template>
  <div class="pt-6">
    <v-row
      dense
      align="center"
      class="mb-3"
    >
      <v-col cols="auto">
        <v-btn
          small
          class="text-none"
          :color="showContentType === CONTENT_TYPE_MUSIC ? 'secondary' : undefined"
          :text="showContentType !== CONTENT_TYPE_MUSIC"
          @click="showContentType === CONTENT_TYPE_MUSIC ? showContentType = undefined : showContentType = CONTENT_TYPE_MUSIC"
        >
          Music
        </v-btn>
        <v-btn
          small
          class="text-none"
          :color="showContentType === CONTENT_TYPE_MARKETING ? 'secondary' : undefined"
          :text="showContentType !== CONTENT_TYPE_MARKETING"
          @click="showContentType === CONTENT_TYPE_MARKETING ? showContentType = undefined : showContentType = CONTENT_TYPE_MARKETING"
        >
          Marketing
        </v-btn>
        <v-btn
          small
          class="text-none"
          :color="showContentType === CONTENT_TYPE_PA ? 'secondary' : undefined"
          :text="showContentType !== CONTENT_TYPE_PA"
          @click="showContentType === CONTENT_TYPE_PA ? showContentType = undefined : showContentType = CONTENT_TYPE_PA"
        >
          PA
        </v-btn>
      </v-col>
      <v-divider vertical />
      <v-col cols="auto">
        <v-btn
          v-for="day in days"
          :key="'select_day_'+day.day"
          :text="!showDays.includes(day.day)"
          class="text-none"
          :color="showDays.includes(day.day) ? 'secondary' : 'grey darken-1'"
          small
          @click="toggleDay(day.day)"
        >
          {{ day.name }}
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-switch
          v-model="showInactive"
          dense
          hide-details
          class="ma-0 pa-0"
        >
          <template v-slot:label>
            <span class="body-2">
              Show inactive clocks
            </span>
          </template>
        </v-switch>
      </v-col>
    </v-row>

    <v-card
      elevation="10"
      class="mb-10"
    >
      <timeline-holder>
        <template v-slot:title>
          Clocks
        </template>
        <div>
          <timeline-clock
            v-for="(clock, index) in filteredClocks"
            :key="'clock_' + clock.id"
            :clock="clock"
            :striped="index % 2 !== 0"
            @edit="editClock"
            @clock:updated="fetchClocks()"
          />
        </div>
      </timeline-holder>
    </v-card>

    <edit-music-clock-dialog
      :visible.sync="editMusicClock.dialog"
      :clock="editMusicClock.clock"
      :radio="radio"
      @clock:updated="fetchClocks()"
      @clock:added="fetchClocks()"
      @clock:deleted="fetchClocks()"
    />

    <edit-marketing-clock-dialog
      :visible.sync="editMarketingClock.dialog"
      :clock="editMarketingClock.clock"
      :radio="radio"
      @clock:updated="fetchClocks()"
      @clock:added="fetchClocks()"
      @clock:deleted="fetchClocks()"
    />

    <edit-pa-clock-dialog
      :visible.sync="editPaClock.dialog"
      :clock="editPaClock.clock"
      :radio="radio"
      @clock:updated="fetchClocks()"
      @clock:added="fetchClocks()"
      @clock:deleted="fetchClocks()"
    />
  </div>
</template>

<script>
// @ is an alias to /src

import Radios from '@/services/Radios'
import TimelineHolder from '@/components/radio/TimelineHolder'
import TimelineClock from '@/components/radio/TimelineClock'
import Clock from '@/models/ClockModel'
import { orderBy } from 'lodash'
import EditMusicClockDialog from '@/components/radio/EditMusicClockDialog'
import { EventBus } from '@/plugins/bus'
import EditMarketingClockDialog from '@/components/radio/EditMarketingClockDialog'
import EditPaClockDialog from '@/components/radio/EditPaClockDialog'

export default {
  name: 'RadioSchedule',
  components: { EditPaClockDialog, EditMarketingClockDialog, EditMusicClockDialog, TimelineClock, TimelineHolder },
  props: {
    radio: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      clocks: {
        loading: false,
        data: []
      },
      showDays: [],
      showContentType: undefined,
      showInactive: true,
      editMusicClock: {
        dialog: false,
        clock: undefined
      },
      editMarketingClock: {
        dialog: false,
        clock: undefined
      },
      editPaClock: {
        dialog: false,
        clock: undefined
      }
    }
  },
  computed: {
    filteredClocks() {
      return this.clocks.data.filter(clock => {
        if (this.showDays.length === 0) return true

        if (clock.playback_type === Clock.PLAYBACK_TYPE_TCP_MESSAGE) return true

        return clock.days.some(d => this.showDays.includes(d))
      }).filter(clock => {
        if (!this.showContentType) return true

        return clock.content_type === this.showContentType
      }).filter(clock => {
        if (this.showInactive) return true

        return !clock.parent_disabled && clock.active
      })
    }
  },
  created() {
    this.fetchClocks()

    this.days = Clock.days

    this.CONTENT_TYPE_MUSIC = Clock.CONTENT_TYPE_MUSIC
    this.CONTENT_TYPE_MARKETING = Clock.CONTENT_TYPE_MARKETING
    this.CONTENT_TYPE_PA = Clock.CONTENT_TYPE_PA

    EventBus.$on(`new-clock:${Clock.CONTENT_TYPE_MUSIC}`, () => {
      this.editMusicClock.clock = undefined
      this.editMusicClock.dialog = true
    })

    EventBus.$on(`new-clock:${Clock.CONTENT_TYPE_MARKETING}`, () => {
      this.editMarketingClock.clock = undefined
      this.editMarketingClock.dialog = true
    })

    EventBus.$on(`new-clock:${Clock.CONTENT_TYPE_PA}`, () => {
      this.editPaClock.clock = undefined
      this.editPaClock.dialog = true
    })
  },
  methods: {
    async fetchClocks() {
      this.clocks.loading = true

      try {
        const result = await Radios.fetchClocks(this.radio.id)

        this.clocks.data = orderBy(result.map(c => {
          c.weight = this.getSortWeight(c.content_type, c.playback_type)
          return c
        }), 'weight')

        // this.$vuetify.goTo(0)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    toggleDay(day) {
      const index = this.showDays.indexOf(day)

      if (index === -1) {
        this.showDays.push(day)
        return
      }

      this.showDays.splice(index, 1)
    },
    getSortWeight(contentType, playbackType) {
      if (contentType === Clock.CONTENT_TYPE_MUSIC) return 1
      if (contentType === Clock.CONTENT_TYPE_MARKETING) return 2
      if (contentType === Clock.CONTENT_TYPE_PA) {
        if (playbackType === Clock.PLAYBACK_TYPE_TCP_MESSAGE) return 4

        return 3
      }

      return 1
    },
    editClock(clock) {
      if (clock.content_type === Clock.CONTENT_TYPE_MUSIC) {
        this.editMusicClock.clock = clock
        this.editMusicClock.dialog = true
      } else if (clock.content_type === Clock.CONTENT_TYPE_MARKETING) {
        this.editMarketingClock.clock = clock
        this.editMarketingClock.dialog = true
      } else if (clock.content_type === Clock.CONTENT_TYPE_PA) {
        this.editPaClock.clock = clock
        this.editPaClock.dialog = true
      }
    }
  }
}
</script>
